/**
 * Frontend utility for logging user activities
 */

// Activity types - keep in sync with backend utils/activityLogger.js
export const ACTIVITY_TYPES = {
  // Authentication
  LOGIN: 'login',
  LOGOUT: 'logout',
  
  // Content Management
  CONTENT_CREATE: 'content_create',
  CONTENT_UPDATE: 'content_update',
  CONTENT_DELETE: 'content_delete',
  CONTENT_VIEW: 'content_view',
  CONTENT_LIKE: 'content_like',
  CONTENT_UNLIKE: 'content_unlike',
  CONTENT_COMMENT: 'content_comment',
  
  // Media
  MEDIA_UPLOAD: 'media_upload',
  MEDIA_DELETE: 'media_delete',
  MEDIA_VIEW: 'media_view',
  
  // Music Features
  PRESET_LOAD: 'preset_load',
  PRESET_SAVE: 'preset_save',
  PRESET_DELETE: 'preset_delete',
  MUSICXML_UPLOAD: 'musicxml_upload',
  MUSICXML_VIEW: 'musicxml_view',
  AUDIO_PLAY: 'audio_play',
  AUDIO_UPLOAD: 'audio_upload',
  RECORDING: 'recording',
  PITCH_GRADING: 'pitch_grading',
  
  // Ear Training
  EAR_TRAINING_START: 'ear_training_start',
  EAR_TRAINING_COMPLETE: 'ear_training_complete',
  EAR_TRAINING_SCORE: 'ear_training_score',

  // Vocal Exercises
  VOCAL_EXERCISE_START: 'vocal_exercise_start',
  VOCAL_EXERCISE_COMPLETE: 'vocal_exercise_complete',
  
  // User Management
  USER_CREATE: 'user_create',
  USER_UPDATE: 'user_update',
  USER_DELETE: 'user_delete',
  PERMISSION_GRANT: 'permission_grant',
  PERMISSION_REVOKE: 'permission_revoke',
  
  // Rehearsals
  REHEARSAL_VIEW: 'rehearsal_view',
  REHEARSAL_CREATE: 'rehearsal_create',
  REHEARSAL_UPDATE: 'rehearsal_update',
  
  // Settings
  SETTINGS_UPDATE: 'settings_update',
  PROFILE_UPDATE: 'profile_update',
  
  // Gallery
  GALLERY_VIEW: 'gallery_view',
  GALLERY_UPLOAD: 'gallery_upload',
  GALLERY_UPDATE: 'gallery_update',
  GALLERY_DELETE: 'gallery_delete',
  
  // Tips
  TIP_VIEW: 'tip_view',
  TIP_CREATE: 'tip_create',
  TIP_UPDATE: 'tip_update',
  TIP_DELETE: 'tip_delete',
  TIP_DISMISS: 'tip_dismiss',
  
  // Issues
  ISSUE_CREATE: 'issue_create',
  ISSUE_UPDATE: 'issue_update',
  ISSUE_DELETE: 'issue_delete',
  ISSUE_ASSIGN: 'issue_assign',
  ISSUE_STATUS_CHANGE: 'issue_status_change',
  
  // API Errors
  API_ERROR: 'api_error'
};

// Resource types - keep in sync with backend utils/activityLogger.js
export const RESOURCE_TYPES = {
  USER: 'user',
  CONTENT: 'content',
  MEDIA: 'media',
  PRESET: 'preset',
  MUSICXML: 'musicxml',
  AUDIO: 'audio',
  REHEARSAL: 'rehearsal',
  GALLERY: 'gallery',
  TIP: 'tip',
  ISSUE: 'issue',
  PERMISSION: 'permission',
  SETTING: 'setting'
};

/**
 * Log a user activity
 * @param {Object} options - Activity logging options
 * @param {Object} options.user - User object with id and role
 * @param {string} options.actionType - Type of action (use ACTIVITY_TYPES constants)
 * @param {string} options.resourceType - Type of resource (use RESOURCE_TYPES constants)
 * @param {string} options.resourceId - ID of the specific resource
 * @param {string} options.description - Human-readable description
 * @param {Object} options.metadata - Additional data specific to the action
 * @param {boolean} options.success - Whether the action completed successfully
 * @param {string} options.errorMessage - Error message if action failed
 * @param {number} options.durationMs - Time taken to complete the action
 */
// Generate or get session ID for anonymous users
function getSessionId() {
  let sessionId = localStorage.getItem('anonymous-session-id');
  if (!sessionId) {
    sessionId = 'anon-' + Math.random().toString(36).substring(2) + '-' + Date.now();
    localStorage.setItem('anonymous-session-id', sessionId);
  }
  return sessionId;
}

// Accepts either an options object or the positional form most callers use:
// logActivity(actionType, resourceType, resourceId, description, metadata, user)
export async function logActivity(...args) {
  const options = typeof args[0] === 'string'
    ? { actionType: args[0], resourceType: args[1], resourceId: args[2],
        description: args[3], metadata: args[4], user: args[5] }
    : (args[0] || {});
  const {
    user = null,
    actionType,
    resourceType = null,
    resourceId = null,
    description,
    metadata = {},
    success = true,
    errorMessage = null,
    durationMs = null
  } = options;
  try {
    const sessionId = user?.id ? null : getSessionId();
    
    const response = await fetch('/api/log-activity', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId: user?.id || null,
        userRole: user?.role || null,
        sessionId,
        actionType,
        resourceType,
        resourceId,
        description,
        metadata,
        success,
        errorMessage,
        durationMs
      })
    });

    if (!response.ok) {
      console.warn('Failed to log activity:', response.statusText);
    }
  } catch (error) {
    // Don't throw - activity logging should never break the main functionality
    console.warn('Activity logging failed:', error);
  }
}

/**
 * Log ear training session start
 */
export function logEarTrainingStart(user, trainingType, config) {
  return logActivity({
    user,
    actionType: ACTIVITY_TYPES.EAR_TRAINING_START,
    resourceType: 'ear_training',
    description: `Started ${trainingType} training session`,
    metadata: {
      trainingType,
      config: {
        questionCount: config.questionCount,
        mode: config.mode,
        instrument: config.instrument,
        enabledIntervals: config.enabledIntervals ? Array.from(config.enabledIntervals) : null,
        enabledChords: config.enabledChords ? Array.from(config.enabledChords) : null,
        enabledOctaves: config.enabledOctaves ? Array.from(config.enabledOctaves) : null
      }
    }
  });
}

/**
 * Log ear training session completion
 */
export function logEarTrainingComplete(user, trainingType, results) {
  return logActivity({
    user,
    actionType: ACTIVITY_TYPES.EAR_TRAINING_COMPLETE,
    resourceType: 'ear_training',
    description: `Completed ${trainingType} training: ${results.correct}/${results.total} correct`,
    metadata: {
      trainingType,
      score: results.correct,
      totalQuestions: results.total,
      percentage: Math.round((results.correct / results.total) * 100),
      roundResults: results.roundResults || []
    }
  });
}

/**
 * Log the start of a vocal exercise (scale playback or guided sequence)
 */
export function logVocalExerciseStart(user, exerciseType, details = {}) {
  return logActivity({
    user,
    actionType: ACTIVITY_TYPES.VOCAL_EXERCISE_START,
    resourceType: 'vocal_exercise',
    description: `Started vocal exercise: ${details.name || exerciseType}`,
    metadata: { exerciseType, ...details }
  });
}

/**
 * Log a guided vocal exercise sequence that ran to completion
 */
export function logVocalExerciseComplete(user, exerciseType, details = {}) {
  return logActivity({
    user,
    actionType: ACTIVITY_TYPES.VOCAL_EXERCISE_COMPLETE,
    resourceType: 'vocal_exercise',
    description: `Completed vocal exercise: ${details.name || exerciseType}`,
    metadata: { exerciseType, ...details }
  });
}

/**
 * Log music file interaction
 */
export function logMusicFileView(user, filename, fileType = 'musicxml') {
  return logActivity({
    user,
    actionType: ACTIVITY_TYPES.MUSICXML_VIEW,
    resourceType: RESOURCE_TYPES.MUSICXML,
    resourceId: filename,
    description: `Loaded ${fileType} file: ${filename}`,
    metadata: {
      filename,
      fileType
    }
  });
}

/**
 * Log audio playback
 */
export function logAudioPlay(user, audioSource, metadata = {}) {
  return logActivity({
    user,
    actionType: ACTIVITY_TYPES.AUDIO_PLAY,
    resourceType: RESOURCE_TYPES.AUDIO,
    description: `Played audio: ${audioSource}`,
    metadata: {
      audioSource,
      ...metadata
    }
  });
}

/**
 * Classify the current device. UA "Mobi" is the standard mobile marker;
 * modern iPads report a Mac UA, so treat Mac + touch as mobile too.
 */
function getDeviceInfo() {
  const ua = navigator.userAgent || '';
  const isMobile = /Mobi/i.test(ua) ||
    (/Mac/.test(ua) && navigator.maxTouchPoints > 1);
  return {
    deviceType: isMobile ? 'mobile' : 'desktop',
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight
  };
}

/**
 * Log page views for anonymous and logged-in users
 */
export function logPageView(user, pageName, metadata = {}) {
  return logActivity({
    user,
    actionType: ACTIVITY_TYPES.CONTENT_VIEW,
    resourceType: RESOURCE_TYPES.CONTENT,
    description: `Viewed ${pageName}`,
    metadata: {
      page: pageName,
      isAnonymous: !user?.id,
      ...getDeviceInfo(),
      ...metadata
    }
  });
}